import { formatDMY } from '../../../utils/dateFormat';
import { fmtEUR } from '../../../utils/moneyFormat';

/**
 * Shared vocabulary for the Documents centre (SUNSKY Documents spec v1.2).
 */

export const SECTIONS = {
  all: {
    title: 'All Documents',
    path: '/documents',
    subtitle: 'Every document type in one overview.',
  },
  invoices: {
    title: 'Invoices',
    path: '/documents/invoices',
    subtitle:
      'Sales invoices, credit and debit notes, payment requests and reminders. Amounts and balances come from Finance.',
  },
  vouchers: {
    title: 'Vouchers',
    path: '/documents/vouchers',
    subtitle: 'Flight documents, hotel, transfer, car rental, visa and cruise vouchers.',
  },
  booking: {
    title: 'Booking Documents',
    path: '/documents/booking-documents',
    subtitle: 'Booking confirmations, services confirmations and insurance documents.',
  },
};

export const DISPATCH_STATUS = {
  not_sent: { label: 'Not sent', color: '#64748b' },
  sent: { label: 'Sent', color: '#059669' },
  share_started: { label: 'WhatsApp share started', color: '#0d9488' },
  sent_other: { label: 'Sent to another recipient only', color: '#b45309' },
  error: { label: 'Unresolved error', color: '#dc2626' },
};

export const DISPATCH_FILTER_OPTIONS = [
  { value: '', label: 'Any dispatch status' },
  { value: 'not_sent', label: 'Not sent' },
  { value: 'sent', label: 'Sent' },
  { value: 'share_started', label: 'WhatsApp share started' },
  { value: 'error', label: 'Unresolved error' },
];

export const PAYMENT_STATUS_OPTIONS = ['Unpaid', 'Partial', 'Paid', 'Refunded', 'Waiting Supplier Refund'];

export const PAYMENT_STATUS_COLOR = {
  Paid: '#059669',
  Partial: '#d97706',
  Unpaid: '#dc2626',
  Refunded: '#2563eb',
  'Waiting Supplier Refund': '#7c3aed',
};

export const CHANNEL_LABEL = {
  email: 'Email',
  whatsapp: 'WhatsApp',
  print: 'Print',
  download: 'Download',
};

// What a dispatch row's status means, in words. "Sent" is all an email
// provider confirms; Delivered and Read only appear when technically confirmed.
export const RESULT = {
  processing: { label: 'Still processing', color: '#d97706' },
  sent: { label: 'Sent', color: '#059669' },
  delivered: { label: 'Delivered', color: '#059669' },
  read: { label: 'Read', color: '#059669' },
  failed: { label: 'Failed', color: '#dc2626' },
  skipped: { label: 'Skipped', color: '#64748b' },
  share_started: { label: 'Share action started', color: '#0d9488' },
  completed: { label: 'Completed', color: '#475569' },
  duplicate: { label: 'Already processed', color: '#64748b' },
};

export const RELEASE_BASIS = {
  paid: 'Fully paid',
  credit: 'Credit arrangement',
  exception: 'Authorised exception',
  not_required: 'No financial release needed',
};

export const HISTORICAL_LABEL = {
  version: 'Historical version',
  superseded: 'Superseded',
  orphaned: 'Booking no longer exists',
};

export const TRAVEL_TYPES = [
  'flight_voucher',
  'hotel_voucher',
  'transfer_voucher',
  'car_rental_voucher',
  'visa_voucher',
  'cruise_voucher',
];

export const formatDate = (value) => formatDMY(value, '-');

export const formatMoney = (value) => (value === null || value === undefined ? '-' : fmtEUR(value));

const STAMP = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'Europe/Brussels',
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
});

/** Processing timestamps always in the one SUNSKY time zone (spec §5). */
export const formatStamp = (value) => {
  if (!value) return '-';
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return STAMP.format(date).replace(',', '');
};

export const hasPermission = (user, key) =>
  !!user?.isAdmin || (Array.isArray(user?.permissions) && user.permissions.includes(key));

export const canSendDocuments = (user) =>
  hasPermission(user, 'document.dispatch.send') || hasPermission(user, 'document.document.create');

export const plural = (n, word, many = `${word}s`) => `${n} ${n === 1 ? word : many}`;
